import { useState } from "react";
import { Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FadeUp } from "@/components/web/Reveal";

const FAQS = [
  {
    q: "What is LinkExpln?",
    a: "LinkExpln is an AI-powered link-safety platform. It analyzes URLs for phishing, scam and malware indicators so you can check whether a link looks safe before you click it.",
  },
  {
    q: "How does LinkExpln scan a link?",
    a: "When you submit a URL, our analysis engine examines signals such as domain reputation and age, redirect chains, lookalike characters, SSL posture and threat-intelligence sources — then produces a plain-language verdict with the reasons behind it.",
  },
  {
    q: "Can LinkExpln detect phishing?",
    a: "LinkExpln is designed to identify common and emerging phishing patterns, including lookalike domains and credential-harvesting pages. No tool catches every attack, so treat verdicts as strong guidance alongside your own judgment.",
  },
  {
    q: "Can it detect scam and suspicious links?",
    a: "Yes. LinkExpln flags indicators associated with scams, fraudulent storefronts and other suspicious behavior, and classifies links as safe, suspicious or dangerous.",
  },
  {
    q: "Does LinkExpln open the website while scanning?",
    a: "You never have to open the link yourself. Analysis happens on our infrastructure — not on your device — so checking a link does not expose you to it.",
  },
  {
    q: "What happens when a dangerous link is detected?",
    a: "You get a clear warning, the specific reasons the link was flagged, and a recommendation not to proceed. Reported malicious links also help protect other users.",
  },
  {
    q: "Can LinkExpln guarantee that every link is safe?",
    a: "No security product can guarantee 100% detection, and we won't claim it. LinkExpln significantly reduces your risk, but a 'safe' verdict is an assessment — always stay cautious with unexpected links.",
  },
  {
    q: "What information does LinkExpln collect?",
    a: "Only what's needed to operate the service: the URLs you submit and any optional details you include in a report. We don't sell your data and we don't use advertising trackers. See our Privacy Policy for details.",
  },
  {
    q: "Is LinkExpln free?",
    a: "Yes — LinkExpln is currently free for everyone during our launch phase.",
  },
  {
    q: "Does LinkExpln work on mobile?",
    a: "The website works in any mobile browser. A dedicated LinkExpln mobile app — with notification link scanning and QR scanning — is on the way.",
  },
  {
    q: "What is LinkExpln Pro?",
    a: "LinkExpln Pro is a planned premium tier with advanced analysis, extended history and priority scanning. It isn't available yet — during the launch phase, everything is free.",
  },
];

export const HomeFaq = () => {
  const [open, setOpen] = useState(null);
  return (
    <section data-testid="faq-section" className="py-24 sm:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <FadeUp className="lg:col-span-4">
          <p className="ls-overline mb-4">FAQ</p>
          <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-white text-balance">
            Questions, answered honestly.
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            Straight answers about what LinkExpln does — and what no
            security tool can promise.
          </p>
        </FadeUp>
        <div className="lg:col-span-8">
          <div className="border-t border-white/10">
            {FAQS.map((f, i) => (
              <div key={f.q} className="border-b border-white/10">
                <button
                  data-testid={`faq-question-${i}`}
                  onClick={() => setOpen(open === i ? null : i)}
                  aria-expanded={open === i}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group focus:outline-none focus:ring-2 focus:ring-[#00C5FE]"
                >
                  <span className="font-heading font-semibold text-base sm:text-lg text-white group-hover:text-[#00C5FE] transition-colors duration-200">
                    {f.q}
                  </span>
                  <Plus
                    size={18}
                    className={`shrink-0 text-[#00C5FE] transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      data-testid={`faq-answer-${i}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <p className="pb-6 text-zinc-400 text-sm sm:text-base leading-relaxed max-w-2xl">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
